import React, { useState, useEffect, useMemo } from "react";
import PreviewTermsConditions from "../../../shared/previewTermsConditions/PreviewTermsConditions";
import styles from "./AcceptTermsAndConditions.module.scss";
import { Button, Grid } from "@mui/material";
import { TERMS_AND_CONDITIONS } from "../../../common/Constants";
import PendingActionPrompt from "../../../shared/pendingActionPrompt/PendingActionPrompt";
import ConfirmDialog from "../../../shared/confirmDialog/ConfirmDialog";
import { withTranslation } from "react-i18next";
import HasPermissionTo from "../../../common/HasPermissionTo";
import TermsAndConditionsClient from "../../../shared/clients/TermsAndConditionsClient";
import PendingActionClient from "../../../shared/clients/PendingActionClient";

const AcceptTermsAndConditions = (props) => {
  const [openDeclinePAPrompt, setOpenDeclinePAPrompt] = useState(false);
  const [openConfirmPAPrompt, setOpenConfirmPAPrompt] = useState(false);
  const [openDeclineDialog, setOpenDeclineDialog] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [dealersActioned, setDealersActioned] = useState([]);
  const [dealersNotActioned, setDealersNotActioned] = useState([]);
  const [tnCStatus, setTnCStatus] = useState({});

  const customText = useMemo(
    () => ({
      actioned: props.t("PendingActionPrompt.actioned"),
      notActioned: props.t("PendingActionPrompt.notActioned"),
      applySelective: props.t("PendingActionPrompt.applySelectiveTerms"),
    }),
    [props]
  );

  const deactivateDialogProps = useMemo(
    () => ({
      title: props.t("BusinessSettings.titleDecline"),
      bodyText: props.user.isLoggedInUserMDLR
        ? props.t("BusinessSettings.bodyTextDeclineWarning")
        : props.t("BusinessSettings.bodyTextDecline"),
      confirm: props.t("BusinessSettings.confirm"),
      cancel: props.t("BusinessSettings.cancel"),
    }),
    [props]
  );

  const confirmDialogProps = useMemo(
    () => ({
      title: props.t("BusinessSettings.titleAccept"),
      bodyText: props.t("BusinessSettings.bodyTextAcceptWarning"),
      confirm: props.t("BusinessSettings.confirm"),
      cancel: props.t("BusinessSettings.cancel"),
    }),
    [props]
  );

  function createSyntheticDealer() {
    if (!props.user || !props.user.commonId) {
      return null;
    }

    return {
      commonId: props.user.commonId,
      completeDealerId: props.user.completeDealerId || props.user.dealerId,
      dealerId: props.user.dealerId,
      name: props.user.dealerName || `Dealer ${props.user.dealerId}`,
      dealerName: props.user.dealerName || `Dealer ${props.user.dealerId}`,
      dealerType: props.user.dealerType || "HUB",
      market: props.user.market,
    };
  }

  function getDealerGroupNonSdlr() {
    if (!props.user) {
      return [];
    }

    if (!props.user.dealerGroup || props.user.dealerGroup.length === 0) {
      const syntheticDealer = createSyntheticDealer();
      return syntheticDealer ? [syntheticDealer] : [];
    }

    const commonIdsString = props.user.getCommonIdsWithoutSDLR();

    if (!commonIdsString || commonIdsString.trim() === "") {
      const currentDealer = props.user.dealerGroup.find(
        (d) =>
          d.commonId === props.user.commonId ||
          d.completeDealerId === props.user.completeDealerId ||
          d.dealerId === props.user.dealerId
      );

      if (currentDealer) {
        return [currentDealer];
      }
      return [props.user.dealerGroup[0]];
    }

    const commonIds = commonIdsString
      .split(",")
      .map((id) => props.user.reformatCommonId(id.trim()));

    return props.user.dealerGroup.filter((d) => commonIds.includes(d.commonId));
  }

  const dealerList = useMemo(() => {
    return getDealerGroupNonSdlr();
  }, [props.user, props.user?.dealerGroup, props.user?.commonId]);

  const onboardedDealerGroup = useMemo(() => {
    return props.user.getOnboardedDealerGroup(true);
  }, [props.user]);

  const termsAndConditions = useMemo(() => {
    if (props.termsAndConditions) {
      return props.termsAndConditions;
    }
    return props.termsAndConditionsHistory[0];
  }, [props.termsAndConditions, props.termsAndConditionsHistory]);

  const hasValidDealers = dealerList && dealerList.length > 0;

  useEffect(() => {
    if (hasValidDealers) {
      const status = getTnCActionStatus();
      setTnCStatus(status);
    }
  }, [dealerList, hasValidDealers]);

  const handleDeclinePAClose = () => setOpenDeclinePAPrompt(false);
  const handleConfirmPAClose = () => setOpenConfirmPAPrompt(false);
  const handleDeclineClose = () => setOpenDeclineDialog(false);
  const handleConfirmClose = () => setOpenConfirmDialog(false);

  const handleConfirmation = (
    termsAndConditions,
    acceptance,
    commonIds,
    dealerIds
  ) => {
    const commonIdsString = Array.isArray(commonIds)
      ? commonIds.join(",")
      : commonIds;

    if (!commonIdsString || commonIdsString.trim() === "") {
      alert("Unable to process: No dealers found. Please refresh the page.");
      return;
    }

    props.hideOrShow(true);
    TermsAndConditionsClient.saveDealerTermsAndConditionsAcceptance(
      props.user,
      termsAndConditions,
      acceptance,
      commonIdsString
    )
      .then(() => {
        if (acceptance) {
          PendingActionClient.removeDealerPendingAction(
            props.user,
            dealerIds,
            TERMS_AND_CONDITIONS,
            1
          ).then(() => {
            props.getPendingActions().then(() => {
              props.alterComponentView(termsAndConditions);
            });
          });
        } else {
          props.alterComponentView(termsAndConditions);
        }
      })
      .catch((error) => {
        console.error("Failed to save terms and conditions:", error);
        alert("Failed to save. Please try again.");
      })
      .finally(() => {
        props.hideOrShow(false);
      });
  };

  const getCondition = () => {
    const isMainViewingSDLR = props.isMainViewingSDLR(props.user);
    if (isMainViewingSDLR) return false;

    const isMainViewingAll = () => {
      return props.user.isLoggedInUserMDLR && props.user.showAll;
    };

    const doesPAOnlyForSDLR = () => {
      const pendingAction = (props.user.pendingActions || []).find(
        (pa) => pa.actionName === TERMS_AND_CONDITIONS
      );

      if (!pendingAction) return false;

      let allRemainingSDLR = true;
      const dealerIdList = pendingAction.attributes?.dealerIds || [];

      dealerIdList.forEach((dealerId) => {
        const dealerType =
          props.user.dealerGroupingsMapByDealerId[dealerId]?.dealerType;
        if (dealerType !== "SDLR") {
          allRemainingSDLR = false;
        }
      });

      return allRemainingSDLR;
    };

    return !(isMainViewingAll() && doesPAOnlyForSDLR());
  };

  const getTnCActionStatus = () => {
    const tnCStatus = {};
    const actioned = [];
    const notActioned = [];

    if (!dealerList || dealerList.length === 0) {
      return tnCStatus;
    }

    dealerList.forEach((dealer) => {
      dealer.dealerName = dealer.name;

      const acceptedDealers =
        props.termsAndConditionsHistory[0]?.acceptedDealers || [];
      const declinedDealers =
        props.termsAndConditionsHistory[0]?.declinedDealers || [];

      if (acceptedDealers.includes(dealer.commonId)) {
        dealer.termsStatus = props.t("PendingActionPrompt.accepted");
        actioned.push(dealer);
        tnCStatus[dealer.commonId] = props.t("ConfirmDialog.accepted");
      } else if (declinedDealers.includes(dealer.commonId)) {
        dealer.termsStatus = props.t("PendingActionPrompt.declined");
        actioned.push(dealer);
        tnCStatus[dealer.commonId] = props.t("ConfirmDialog.rejected");
      } else {
        notActioned.push(dealer);
      }
    });

    setDealersActioned(actioned);
    setDealersNotActioned(notActioned);

    return tnCStatus;
  };

  const isMainViewingAll = () => {
    return (
      props.user.isLoggedInUserMDLR &&
      onboardedDealerGroup.length > 1 &&
      props.user.showAll
    );
  };

  const isMainViewingBranch = () => {
    return (
      props.user.isLoggedInUserMDLR &&
      onboardedDealerGroup.length > 1 &&
      !props.user.showAll
    );
  };

  const openDialog = (acceptance) => {
    if (!hasValidDealers) {
      alert("No dealers available. Please refresh the page.");
      return;
    }

    if (isMainViewingAll()) {
      acceptance ? setOpenConfirmPAPrompt(true) : setOpenDeclinePAPrompt(true);
    } else if (isMainViewingBranch()) {
      acceptance ? setOpenConfirmDialog(true) : setOpenDeclineDialog(true);
    } else {
      const commonIds = dealerList.map((d) => d.commonId);
      const dealerIds = dealerList.map((d) => d.completeDealerId || d.dealerId);

      if (acceptance) {
        handleConfirmation(termsAndConditions, true, commonIds, dealerIds);
      } else {
        setOpenDeclineDialog(true);
      }
    }
  };

  const applyAll = (termsAndCond, acceptance) => {
    const commonIds = dealerList.map((d) => d.commonId);
    const dealerIds = dealerList.map((d) => d.completeDealerId || d.dealerId);
    handleConfirmation(termsAndCond, acceptance, commonIds, dealerIds);
  };

  const applySelective = (termsAndCond, acceptance) => {
    const commonIds = [];
    const dealerIds = [];

    dealerList.forEach((dealer) => {
      const isActioned = dealersActioned.some(
        (d) => d.commonId === dealer.commonId
      );
      if (!isActioned) {
        commonIds.push(dealer.commonId);
        dealerIds.push(dealer.completeDealerId || dealer.dealerId);
      }
    });

    handleConfirmation(termsAndCond, acceptance, commonIds, dealerIds);
  };

  return (
    <div>
      <div className="pagePadding">
        <Button
          style={{ backgroundColor: "transparent" }}
          onClick={() => props.alterComponentView(termsAndConditions)}
        >
          <div className={styles.backButton}>
            <div className={styles.arrowLeft}>&nbsp;</div>
            <div>
              {props.fromPending
                ? props.t("BusinessSettings.backToDashboard")
                : props.t("BusinessSettings.backToEmp")}
            </div>
          </div>
        </Button>
        <div className={styles.termsConditionsHeader}>
          {props.t("BusinessSettings.termsTitle")}
        </div>
        <div className={styles.previewPDFWrapper}>
          <div className={styles.previewPDFTitle}>
            {props.user.market.toUpperCase() +
              " " +
              props.t("BusinessSettings.termsHeader") +
              termsAndConditions.version +
              ".0"}
          </div>
          <div className={styles.effectiveFrom}>
            {props.t("BusinessSettings.termsEffectiveDate")}{" "}
            {termsAndConditions.effectiveDate})
          </div>
          <PreviewTermsConditions
            className={styles.previewPDF}
            file={termsAndConditions.file}
          />
        </div>
        <div className={styles.downloadNewVersion}>
          <span
            onClick={() =>
              props.handleTermsAndConditionsDownload(termsAndConditions)
            }
          >
            {props.t("BusinessSettings.downloadTerms")}
          </span>
        </div>
      </div>

      <PendingActionPrompt
        {...props}
        disabled={false}
        customText={customText}
        showDeclineWarning={true}
        dealersActioned={dealersActioned}
        dealersNotActioned={dealersNotActioned}
        title={props.t("BusinessSettings.termsTitle")}
        buttonTitle={props.t("BusinessSettings.termsTitle")}
        onClose={handleDeclinePAClose}
        open={openDeclinePAPrompt}
        applyAll={() => applyAll(termsAndConditions, false)}
        applySome={() => applySelective(termsAndConditions, false)}
      />
      <PendingActionPrompt
        {...props}
        disabled={false}
        customText={customText}
        dealersActioned={dealersActioned}
        dealersNotActioned={dealersNotActioned}
        title={props.t("BusinessSettings.termsTitle")}
        buttonTitle={props.t("BusinessSettings.termsTitle")}
        onClose={handleConfirmPAClose}
        open={openConfirmPAPrompt}
        applyAll={() => applyAll(termsAndConditions, true)}
        applySome={() => applySelective(termsAndConditions, true)}
      />

      <ConfirmDialog
        {...props}
        dialogProps={deactivateDialogProps}
        showWarning={props.user.isLoggedInUserMDLR}
        dealerList={dealerList}
        open={openDeclineDialog}
        onConfirm={() =>
          handleConfirmation(
            termsAndConditions,
            false,
            dealerList.map((d) => d.commonId),
            dealerList.map((d) => d.completeDealerId || d.dealerId)
          )
        }
        onCancel={handleDeclineClose}
        onClose={handleDeclineClose}
        attributes={tnCStatus}
        boldTitle={false}
      />
      <ConfirmDialog
        {...props}
        dialogProps={confirmDialogProps}
        showWarning={true}
        dealerList={dealerList}
        open={openConfirmDialog}
        onConfirm={() =>
          handleConfirmation(
            termsAndConditions,
            true,
            dealerList.map((d) => d.commonId),
            dealerList.map((d) => d.completeDealerId || d.dealerId)
          )
        }
        onCancel={handleConfirmClose}
        onClose={handleConfirmClose}
        attributes={tnCStatus}
        boldTitle={false}
      />

      <HasPermissionTo
        perform={["acceptTermsAndConditions"]}
        permissions={props.user.permissions.rolePermissions}
        condition={getCondition()}
        render={() => (
          <>
            <div
              className={`${styles.acceptanceSelector} pagePaddingTop pagePaddingBottom primary-text-bold-color`}
            >
              {props.t("BusinessSettings.termsAccept")}
            </div>

            <Grid item md={12}>
              <div className={styles.dialogActions}>
                <Button
                  onClick={() => openDialog(false)}
                  className="cancelledButton dialog"
                  disabled={!hasValidDealers}
                >
                  {props.t("BusinessSettings.decline")}
                </Button>
                <Button
                  onClick={() => openDialog(true)}
                  className="confirmButton dialog"
                  disabled={!hasValidDealers}
                >
                  {props.t("BusinessSettings.accept")}
                </Button>
              </div>
            </Grid>
          </>
        )}
      />
    </div>
  );
};

export default withTranslation("emp")(AcceptTermsAndConditions);
